/**
 * Email Verification Pending Page
 * 
 * This page is shown to users who have registered but haven't verified their email yet.
 * Features:
 * - Display user's email
 * - Resend verification email with cooldown
 * - Manual verification check
 * - Logout option
 */

import React from "react";
import {
  Button,
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import { useDispatch } from "react-redux";
import { logout } from "../../../store/actions/authActions";
import { useEmailVerification } from "../../../hooks/useEmailVerification";
import Logo from "../../../assets/img/custom/logo.png";

const EmailVerificationPending = () => {
  const dispatch = useDispatch();
  const {
    userEmail,
    isResending,
    cooldownSeconds,
    canResend,
    resendVerificationEmail,
  } = useEmailVerification();

  const handleLogout = () => {
    // Dispatch clears Redux state immediately; App.js routes to /auth/login
    dispatch(logout());
  };

  return (
    <Container className="mt-8 pb-5" style={{ minHeight: "100vh" }}>
      <Row className="justify-content-center">
        <Col lg="6" md="8">
          {/* Logo */}
          <div className="text-center mb-4">
            <a href="https://fans-help-players-athlete.web.app/">
              <img src={Logo} alt="logo" className="logo" style={{ maxWidth: "200px" }} />
            </a>
          </div>

          <Card className="shadow border-0">
            <CardBody className="px-lg-5 py-lg-5">
              {/* Icon */}
              <div className="text-center mb-4">
                <div
                  className="icon icon-shape bg-gradient-primary text-white rounded-circle shadow mb-3"
                  style={{ width: "80px", height: "80px", fontSize: "40px", margin: "0 auto" }}
                >
                  <i className="ni ni-email-83"></i>
                </div>
                <h2 className="text-primary mb-0">Verify Your Email</h2>
              </div>

              {/* Message */}
              <div className="text-center mb-4">
                <p className="text-muted mb-3">
                  We've sent a verification email to:
                </p>
                <p className="font-weight-bold text-dark h5 mb-3">{userEmail}</p>
                <p className="text-muted small">
                  Please check your inbox and click the verification link to activate your account.
                  If you don't see the email, check your spam or junk folder.
                </p>
              </div>

              <hr className="my-4" />

              {/* Action Buttons */}
              <div className="d-flex flex-column gap-3">
                {/* Resend Email Button */}
                <Button
                  color="primary"
                  size="lg"
                  block
                  onClick={resendVerificationEmail}
                  disabled={!canResend || isResending}
                  className="mb-2"
                >
                  {isResending ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Sending...
                    </>
                  ) : cooldownSeconds > 0 ? (
                    <>
                      <i className="ni ni-time-alarm mr-2"></i>
                      Resend in {cooldownSeconds}s
                    </>
                  ) : (
                    <>
                      <i className="ni ni-email-83 mr-2"></i>
                      Resend Verification Email
                    </>
                  )}
                </Button>

                {/* Logout Button */}
                <Button
                  color="secondary"
                  size="lg"
                  block
                  outline
                  onClick={handleLogout}
                >
                  <i className="ni ni-user-run mr-2"></i>
                  Logout
                </Button>
              </div>

              {/* Help Text */}
              <div className="mt-4 text-center">
                <p className="text-muted small mb-0">
                  <i className="fas fa-info-circle mr-1"></i>
                  Need help? Contact support at{" "}
                  <a href="mailto:FansHelpPlayers@gmail.com">
                    FansHelpPlayers@gmail.com
                  </a>
                </p>
              </div>
            </CardBody>
          </Card>

          {/* Additional Info */}
          <div className="mt-4 text-center">
            <small className="text-muted">
              <i className="fas fa-shield-alt mr-1"></i>
              Email verification helps keep your account secure
            </small>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default EmailVerificationPending;
